"use client";

import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { RemoteImage } from "@/components/ui/RemoteImage";

type HeroSize = "md" | "lg";

const HERO_BAND: Record<HeroSize, string> = {
  md: "h-[280px] lg:h-[400px]",
  lg: "h-[600px]",
};

/* Every content page in the kit opens the same way: a tall image band that
   dissolves into the canvas, then the title stack sitting over the seam.
   The band shows the page's own image when it has one — a race's artwork
   from the platform, say — and the kit's placeholder mark when it does not. */
export function PageHero({
  title,
  body,
  eyebrow,
  image,
  size = "md",
  children,
}: {
  title: string;
  body?: string;
  eyebrow?: ReactNode;
  image?: string;
  size?: HeroSize;
  children?: ReactNode;
}) {
  return (
    <section className="relative">
      <div className={cn("hero-wash relative overflow-hidden", HERO_BAND[size])}>
        {image ? (
          <>
            <RemoteImage src={image} alt="" sizes="100vw" priority />
            <span className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/40 to-transparent" />
          </>
        ) : (
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-fg-disabled">
            <Icon name="image" size={35} />
          </span>
        )}
      </div>

      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: EASE }}
        className="relative z-10 mx-auto flex max-w-[1096px] flex-col items-center gap-[12px] px-[24px] py-[32px] text-center lg:px-[48px]"
      >
        <h1 className="text-h4 font-black lg:text-h1">{title}</h1>
        {eyebrow}
        {body && <p className="w-full text-[15.75px] font-normal leading-[1.6] text-fg-muted lg:text-[17.5px]">{body}</p>}
        {children}
      </motion.div>
    </section>
  );
}
